// ---------------------------------------------------------------------------
// Project persistence in IndexedDB. The original folder (showDirectoryPicker,
// see project.ts) becomes just the initial importer: once a project is
// opened, everything lives here (debounced autosave, see App.tsx) and the
// app never touches the original folder or handle again — the user can
// move/delete the source folder without it affecting the already-imported
// project.
//
// `EvalHidProject` (types.ts) is kept "serializable on purpose" (see its own
// comment) precisely for this: structured clone (the algorithm IndexedDB
// uses, not JSON) natively supports Map, and the input series (DailySeries,
// dailySeries.ts) store each column in a Float64Array — also native to
// structured clone, as binary, without going through text. Nothing needs
// converting to save/load.
//
// Two object stores:
//   - "meta": a lightweight per-project record (id/name/date) — to list
//     "Your projects" in Landing.tsx without loading the full data, which
//     can weigh tens of MB per real project (see dailySeries.ts: a real Turia
//     project is around 10MB per input series).
//   - "data": everything else (EvalHidProject + series + hand-drawn layers)
//     — only read/written by id, when opening/autosaving a specific
//     project, never when listing. Renamed from the original Spanish
//     "datos" (DB_VERSION 1->2) — see EvalHidDBSchema's own comment for the
//     migration that keeps every already-saved project readable.
// ---------------------------------------------------------------------------
import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import {
  enumerateWindows,
  sourceRanges,
  type DateRange,
  type StationSelectionMethod,
} from "./aemet/aemet.ts";
import { concatSeriesBatches, type DailySeries } from "./dailySeries.ts";
import { chunkSeriesColumns } from "./seriesBatching.ts";
import type { BatchedSeriesKey, SeriesBatchSlice } from "./exportProjectWorkerMessages.ts";
import type { AquiferLayer, OutletPointLayer, SubBasinLayer } from "./gisLayer.ts";
import type { GenericLayer } from "./genericLayer.ts";
import type { EvalHidProject } from "./types.ts";

const DB_NAME = "evalhid";
const DB_VERSION = 6;

export interface ProjectMeta {
  id: string;
  name: string;
  lastModified: number;
  /**
   * Examples (see examples.ts) autosave just like any other project, without
   * a separate flow — but flagged this way so they don't get confused with
   * real saved projects: `listProjects()` excludes them from "Your
   * projects" (Landing.tsx) and `purgeEphemeralProjects()` deletes them on
   * app startup (see App.tsx). "Save copy as" (TopBar.tsx) on one of them
   * creates a new record with `ephemeral: false`, which does persist.
   */
  ephemeral: boolean;
}

/**
 * One sub-basin's "Fetch from AEMET" result (AemetFetchDialog.tsx/
 * useAemetFetch.ts) — which station(s) actually supplied its precipitation/
 * temperature/ETP, day by day, kept so the source stays checkable later
 * (AemetProvenanceDialog.tsx, opened from a button next to "Fetch from
 * AEMET" — which itself stops showing once this exists, see
 * EditSubBasinDialog.tsx), not just in the moment right after fetching —
 * applying is automatic now (no "review, then click Apply" step),
 * precisely because this record is what lets the user check afterward
 * instead of having to watch it happen.
 *
 * Each `sourceByDate` entry is a `Map<source, DateRange[]>` — contiguous
 * same-source runs (`sourceRanges`, aemet.ts), NOT a per-day
 * `Map<date, source>` like the in-progress fetch itself works with
 * internally (`BuiltSeries.sourceByDate`, aemet.ts) — this is the one and
 * only shape ever actually shown (`SourceCoverageDetail.tsx` renders
 * exactly these grouped ranges, never a raw per-day tally). Real incident
 * (2026-08-27): storing the per-day form here meant a Duero-scale project
 * (1728 sub-basins × ~17 years of ROCIO data) held ~22 MILLION Map entries
 * in `aemetProvenance` alone — a 5+ second structured-clone freeze on every
 * project open, and on every autosave/close (`aemetProvenance` lives in the
 * "data" IndexedDB store, written on every save regardless of whether
 * `series` itself changed). A real download's coverage is overwhelmingly a
 * handful of contiguous runs (one clean fetch = ONE run per variable), so
 * this is a 1000x+ reduction in the common case, not just in theory.
 * `mergeProvenance`/`mergeSourceByDate` (aemetApply.ts) expand a stored
 * run list back to a per-day map only when actually folding in a fresh
 * incremental fetch (extend mode, or a retry) — never for the whole batch
 * at once.
 * Structured clone (IndexedDB, and exportProject.ts's own replacer/
 * reviver, see its header comment) already supports Map values natively,
 * same as EvalHidProject.untypedTables — unchanged by this, only the
 * VALUES inside each Map changed shape (`DateRange[]` instead of
 * `string | null`).
 */
export interface AemetProvenance {
  stationsUsed: { code: string; name: string; distanceKm: number }[];
  /** Which method produced this — "nearest"/"idw" (AEMET stations) or "rocio" (grid cells, `stationsUsed`' codes are cell ids). Optional so old, already-persisted provenance without this field (from before it existed) reads as "nearest" wherever it's checked, rather than crashing on `undefined`. */
  method?: StationSelectionMethod;
  /** "rocio" only: AEMET's OWN publish date for the grid (its source file's HTTP `Last-Modified`, see rocio.ts's `RocioManifest.sourceUpdatedAt`) — shown instead of `fetchedAt` for "how fresh is this data", since `fetchedAt` only says when THIS APP queried it, not how recently AEMET itself updated the grid. Absent for station-based methods and for old rocio provenance fetched before this field existed. */
  sourceUpdatedAt?: string;
  fetchedAt: string;
  dateRange: { start: string; end: string };
  sourceByDate: {
    precipitation: Map<string | null, DateRange[]>;
    temperature: Map<string | null, DateRange[]>;
    etp: Map<string | null, DateRange[]>;
  };
  /** Date-window requests that failed even after retries (aemet.ts) — the days within them are already reflected as gaps in `sourceByDate` too; this is what a later retry (aemetRetry.ts) re-fetches, and what lets the "N requests couldn't be completed" warning list exactly which date ranges. */
  failedWindows: DateRange[];
}

/**
 * Which MITECO layer + code a sub-basin was imported from (SubBasinsPanel.tsx's
 * "Add from MITECO", see App.tsx's addSelectedMiteco) — the only reliable way
 * to tell a later import round "this one's already in the project" and skip
 * it: MITECO's polygons are independently simplified from whatever shapefile
 * the project itself might have been built from, so they'd essentially never
 * compare equal geometry-wise (unlike "recover deleted sub-basins", which can
 * rely on that). `basinCode` alone isn't unique across the two layers (river
 * basins vs. sub-basins are different MITECO datasets, different code
 * spaces), hence `layer` is part of the key too.
 */
export interface MitecoOrigin {
  layer: "basins" | "subBasins";
  basinCode: string;
}

export interface ProjectData {
  id: string;
  project: EvalHidProject;
  series: {
    precipitation: DailySeries | null;
    etp: DailySeries | null;
    temperature: DailySeries | null;
    pumping: DailySeries | null;
  };
  gisLayer: SubBasinLayer | null;
  outletPointsLayer: OutletPointLayer;
  aquifersLayer: AquiferLayer;
  /** Purely visual reference layers — see genericLayer.ts's own header comment. Optional — undefined for any project saved before this feature existed. */
  genericLayers?: GenericLayer[];
  /** Keyed by SubBasin.subBasinCode. Optional — undefined for any project saved before this feature existed, or for a sub-basin whose series never came from AEMET. */
  aemetProvenance?: Record<number, AemetProvenance>;
  /** Keyed by SubBasin.subBasinCode. Optional — undefined for any project saved before this feature existed, or for a sub-basin that didn't come from MITECO. */
  mitecoOrigin?: Record<number, MitecoOrigin>;
  /**
   * Observed (real, measured) daily flow, one column per outlet point name —
   * for the calibration panel to compare against a simulated run
   * (CalibrationPanel.tsx). A "solo web" extension like `mitecoOrigin` above,
   * not `series` (LoadedProject/`ProjectSeries`): unlike precipitation/ETP/
   * temperature/pumping, this never comes from a real `.ehd`/folder, only
   * ever from pasting/uploading it by hand into `EditOutletPointDialog`'s own
   * "Observado" tab — same reason it's matched by outlet point NAME, not an
   * index column like `SubBasin.precipCol`, mirroring how `series.pumping`
   * is matched by aquifer name. Optional — undefined for any project saved
   * before this feature existed, or one with no observed series at all.
   */
  observedFlow?: DailySeries | null;
}

/** `ProjectMeta` builder — `useProjectPersistence.ts` builds one of these at 3 call sites (new project, close, autosave), always with the same shape (`lastModified` stamped fresh each time). */
export function buildProjectMeta(id: string, name: string, ephemeral: boolean): ProjectMeta {
  return { id, name, lastModified: Date.now(), ephemeral };
}

/** `ProjectData` builder for the fields NOT covered by `project`/`series` (see `LoadedProject`, project.ts) — same 3 call sites as `buildProjectMeta` above build the rest identically. */
export function buildProjectData(
  id: string,
  loadedProject: { project: EvalHidProject; series: ProjectData["series"] },
  fields: {
    gisLayer: SubBasinLayer | null;
    outletPointsLayer: OutletPointLayer;
    aquifersLayer: AquiferLayer;
    genericLayers: GenericLayer[];
    aemetProvenance: Record<number, AemetProvenance>;
    mitecoOrigin: Record<number, MitecoOrigin>;
    observedFlow: DailySeries | null;
  }
): ProjectData {
  return {
    id,
    project: loadedProject.project,
    series: loadedProject.series,
    ...fields,
  };
}

interface EvalHidDBSchema extends DBSchema {
  meta: { key: string; value: ProjectMeta };
  /**
   * Renamed to "data" (DB_VERSION 1->2), and the store itself deleted
   * (DB_VERSION 3->4, see upgrade()'s own `oldVersion < 4` step) — kept
   * declared here purely so the 1->2 migration below can still type-check
   * reading a v1 database's records under this original Spanish name. Never
   * written to, and no longer exists as a real object store in any database
   * that's gone through the 3->4 upgrade.
   */
  datos: { key: string; value: ProjectData };
  /**
   * No longer holds `series` itself (DB_VERSION 4->5, see upgrade()'s own
   * `oldVersion < 5` step and `saveProject`'s own comment on why) — every
   * record here is really `Omit<ProjectData, "series">`, but `idb`'s schema
   * typing doesn't have a clean way to express "this store's value type
   * changed shape at a given version," so this stays typed as the full
   * `ProjectData` and `saveProject`/`loadSavedProject` are what actually
   * enforce the split at the boundary.
   */
  data: { key: string; value: ProjectData };
  /**
   * Split out of "data" (DB_VERSION 4->5) — a real project's series can be
   * the large majority of its stored bytes (a real Turia project's single
   * input series is ~10MB), but most edits (renaming a zone, drawing a
   * sub-basin, adjusting a parameter...) never touch it. Before this split,
   * EVERY autosave re-wrote the whole series blob regardless, since
   * IndexedDB's `put()` always (re)serializes the entire value passed to
   * it — there's no partial/patch update. Keyed by project id, same as
   * "data" and "meta".
   *
   * Superseded by "seriesBatch"/"seriesExtra" below (DB_VERSION 5->6) —
   * left in place (not deleted), same reasoning as "datos" above: a safety
   * net for one version, removed for real later once the split is
   * confirmed solid. `saveProject`/`loadSavedProject` no longer read or
   * write it.
   */
  series: { key: string; value: { id: string; series: ProjectData["series"] } };
  /**
   * One row per sub-basin BATCH per project (DB_VERSION 5->6,
   * see seriesBatching.ts's own header) — even "series" above, despite
   * already being its own store, still meant a SINGLE `put()` cloning every
   * sub-basin's columns together in one shot; at Duero scale (1728
   * sub-basins, ~1GB across precipitation/temperature/ETP) that single
   * clone is itself what threw "Data cannot be cloned, out of memory" (the
   * same class of problem exportProjectWorkerMessages.ts's own header
   * describes for the Worker side of this). Compound key `[id, batchIndex]`
   * — `seriesBatchKeyRange(id)` reads/deletes every batch of one project at
   * once. `pumping` isn't part of this store at all — see "seriesExtra"'s
   * own comment.
   */
  seriesBatch: { key: [string, number]; value: SeriesBatchRow };
  /**
   * The one thing about a project's series that ISN'T batched (DB_VERSION
   * 5->6) — each batched series type's own shared date axis (small: a
   * handful of `dates` arrays, not per-sub-basin) and `pumping` whole
   * (matched by NAME — a sub-basin's own column, or an aquifer's — not
   * batched by sub-basin code at all, and in practice sparse; same
   * reasoning `exportProjectWorkerMessages.ts`'s `ExportWorkerInitMessage`
   * gives for treating it the same way). One row per project, keyed by id.
   */
  seriesExtra: { key: string; value: SeriesExtraRow };
}

interface SeriesBatchRow {
  id: string;
  batchIndex: number;
  precipitation?: SeriesBatchSlice;
  etp?: SeriesBatchSlice;
  temperature?: SeriesBatchSlice;
}

interface SeriesExtraRow {
  id: string;
  /** Absent entries mean that series type was never loaded at all — same convention as `ExportWorkerInitMessage.dates`. */
  dates: Partial<Record<BatchedSeriesKey, string[]>>;
  pumping: DailySeries | null;
}

/** Every row of one project's sub-basin batches, in "seriesBatch" — `IDBKeyRange.bound`'s `-Infinity`/`Infinity` on the numeric half of a compound key is a standard IndexedDB idiom for "every value of this key's second component", same as bounding a date range elsewhere in this app (aemet.ts). */
function seriesBatchKeyRange(id: string): IDBKeyRange {
  return IDBKeyRange.bound([id, -Infinity], [id, Infinity]);
}

/**
 * Builds the rows `saveProject`/the DB_VERSION 5->6 migration both write —
 * pulled out as a pure function (no `tx`/IndexedDB calls of its own) so
 * both callers stay simple, and so it's directly unit-testable. Each series
 * type chunked independently (see seriesBatching.ts's own header for why
 * NOT by matching sub-basin code against column name) — they can have
 * different column counts, so batch `i`'s row may carry a `precipitation`
 * slice, an `etp` slice, both, or neither, entirely independent of each
 * other.
 */
function buildSeriesRows(
  id: string,
  series: ProjectData["series"]
): { batches: SeriesBatchRow[]; extra: SeriesExtraRow } {
  const precipChunks = chunkSeriesColumns(series.precipitation);
  const etpChunks = chunkSeriesColumns(series.etp);
  const tempChunks = chunkSeriesColumns(series.temperature);
  const totalBatches = Math.max(precipChunks.length, etpChunks.length, tempChunks.length);
  const batches: SeriesBatchRow[] = Array.from({ length: totalBatches }, (_, batchIndex) => {
    const row: SeriesBatchRow = { id, batchIndex };
    if (precipChunks[batchIndex]) row.precipitation = precipChunks[batchIndex];
    if (etpChunks[batchIndex]) row.etp = etpChunks[batchIndex];
    if (tempChunks[batchIndex]) row.temperature = tempChunks[batchIndex];
    return row;
  });
  const extra: SeriesExtraRow = {
    id,
    dates: {
      ...(series.precipitation ? { precipitation: series.precipitation.dates } : {}),
      ...(series.etp ? { etp: series.etp.dates } : {}),
      ...(series.temperature ? { temperature: series.temperature.dates } : {}),
    },
    pumping: series.pumping,
  };
  return { batches, extra };
}

/** Inverse of `buildSeriesRows` — reassembles one project's batched rows (already read from "seriesBatch"/"seriesExtra") back into the combined `ProjectData["series"]` shape the rest of the app expects; `null`/`EMPTY_SERIES` are the caller's job (see `loadSavedProject`), not this function's, since a missing `extra` row means something different (never saved with series at all) than an empty `dates` entry within one that exists. */
function reassembleSeries(
  extra: SeriesExtraRow,
  batchRows: readonly SeriesBatchRow[]
): ProjectData["series"] {
  const sorted = [...batchRows].sort((a, b) => a.batchIndex - b.batchIndex);
  function reassembleOne(key: BatchedSeriesKey): DailySeries | null {
    const dates = extra.dates[key];
    if (!dates) return null;
    const slices = sorted.map((row) => row[key]).filter((s): s is SeriesBatchSlice => s != null);
    return concatSeriesBatches(dates, slices);
  }
  return {
    precipitation: reassembleOne("precipitation"),
    etp: reassembleOne("etp"),
    temperature: reassembleOne("temperature"),
    pumping: extra.pumping,
  };
}

/** Safe fallback for `loadSavedProject` if a "data" record's matching "series" record is somehow missing — should never happen in practice (see `saveProject`'s own comment on the invariant that prevents it), but returning this instead of `undefined` avoids a crash deeper in `DailySeries`-shaped code for a corrupted/manually-edited database. */
const EMPTY_SERIES: ProjectData["series"] = {
  precipitation: null,
  etp: null,
  temperature: null,
  pumping: null,
};

let dbPromise: Promise<IDBPDatabase<EvalHidDBSchema>> | null = null;

/**
 * Fixes a project saved before `MitecoOrigin.codCuenca`/`layer`'s Spanish
 * values were renamed to `basinCode`/`"basins"|"subBasins"` (see storage.ts's
 * own git history) — structured clone doesn't enforce this file's current
 * TypeScript shapes, so an old record can still carry the original Spanish
 * key/values at runtime even though `MitecoOrigin` only declares the English
 * ones now. Without this, `mitecoOrigin`'s "already imported from MITECO,
 * don't offer it again" check (MitecoSelectableLayer.tsx) would silently
 * stop recognizing every sub-basin a project like this already imported —
 * `layer` is part of that lookup's key (see MitecoOrigin's own comment), so
 * a stale Spanish value would break the match just as surely as a stale
 * `codCuenca`. Returns the same object unchanged if there's nothing to fix,
 * so callers can always `put()` the result without a wasted write.
 */
function migratedMitecoOrigin(
  mitecoOrigin: ProjectData["mitecoOrigin"]
): ProjectData["mitecoOrigin"] {
  if (!mitecoOrigin) return mitecoOrigin;
  let changed = false;
  const fixed: Record<number, MitecoOrigin> = {};
  for (const [code, origin] of Object.entries(mitecoOrigin)) {
    const raw = origin as unknown as {
      layer: "cuencas" | "subcuencas" | MitecoOrigin["layer"];
      codCuenca?: string;
    };
    const layer =
      raw.layer === "cuencas" ? "basins" : raw.layer === "subcuencas" ? "subBasins" : raw.layer;
    const basinCode = origin.basinCode ?? raw.codCuenca;
    if (layer !== raw.layer || basinCode !== origin.basinCode) {
      fixed[Number(code)] = { layer, basinCode: basinCode as string };
      changed = true;
    } else {
      fixed[Number(code)] = origin;
    }
  }
  return changed ? fixed : mitecoOrigin;
}

/**
 * Fixes `SubBasin.outletCode`'s old `0`-as-"unassigned" sentinel to `null`
 * (DB_VERSION 2->3) — matching `Aquifer.outletCode`'s own `null` sentinel
 * for the exact same foreign key (`OutletPoint.outletCode`), inconsistent
 * before this (see types.ts's own comment on `SubBasin.outletCode`, and the
 * 2026-08-24 structure/maintainability review that found it). Same
 * "return the same reference if there's nothing to fix" shape as
 * `migratedMitecoOrigin` above, so callers can always `put()` the result
 * without a wasted write when a project has nothing to migrate.
 */
function migratedOutletCode0ToNull(
  subBasins: EvalHidProject["subBasins"]
): EvalHidProject["subBasins"] {
  if (!subBasins.some((c) => c.outletCode === 0)) return subBasins;
  return subBasins.map((c) => (c.outletCode === 0 ? { ...c, outletCode: null } : c));
}

/**
 * Backfills `EvalHidProject.seriesScale` (new field) on a project saved
 * before it existed — every already-saved project's own series ARE daily
 * (it's the only scale this app has ever supported until now), so `"daily"`
 * is the correct value here, not just a safe placeholder. `project.seriesScale`
 * reads as `undefined` at runtime on an old record despite the type saying
 * otherwise (structured clone/JSON.parse don't enforce TypeScript shapes).
 */
function migratedSeriesScale(project: EvalHidProject): EvalHidProject {
  if (project.seriesScale) return project;
  return { ...project, seriesScale: "daily" };
}

/**
 * Backfills `AemetProvenance.sourceByDate` (per-series Map) from its OLD
 * per-day `Map<date, source>` shape to the current grouped-runs
 * `Map<source, DateRange[]>` shape — see `AemetProvenance`'s own comment
 * (the Duero-scale incident this avoids). Detected by checking one entry's
 * VALUE, not by any version flag: the old shape's values are `string | null`
 * (a single source), the new shape's are always an array (`DateRange[]`,
 * possibly empty but never a string/null) — an empty Map (no data fetched
 * for that variable at all) is indistinguishable either way and correctly
 * a no-op regardless of which shape it's "supposed" to be.
 */
function migratedAemetProvenance(
  aemetProvenance: Record<number, AemetProvenance>
): Record<number, AemetProvenance> {
  let changed = false;
  const result: Record<number, AemetProvenance> = {};
  for (const [key, provenance] of Object.entries(aemetProvenance)) {
    // Lazy on purpose: `enumerateWindows` over a real multi-year range is
    // real work (one `addDaysIso` call per day) — only pay for it the one
    // time an old-shape map actually needs re-grouping, never for the
    // already-migrated common case (a real regression this caused once,
    // computing it unconditionally for all 1728 Duero-scale records even
    // though none of them needed it).
    let dates: string[] | null = null;
    const migrateOne = (
      byDate: Map<string, string | null> | Map<string | null, DateRange[]>
    ): Map<string | null, DateRange[]> => {
      const firstValue = byDate.values().next().value;
      if (firstValue === undefined || Array.isArray(firstValue)) {
        return byDate as Map<string | null, DateRange[]>;
      }
      changed = true;
      dates ??= enumerateWindows(provenance.dateRange, 1).map((w) => w.start);
      return sourceRanges(byDate as Map<string, string | null>, dates);
    };
    const precipitation = migrateOne(provenance.sourceByDate.precipitation);
    const temperature = migrateOne(provenance.sourceByDate.temperature);
    const etp = migrateOne(provenance.sourceByDate.etp);
    result[Number(key)] =
      precipitation === provenance.sourceByDate.precipitation &&
      temperature === provenance.sourceByDate.temperature &&
      etp === provenance.sourceByDate.etp
        ? provenance
        : { ...provenance, sourceByDate: { precipitation, temperature, etp } };
  }
  return changed ? result : aemetProvenance;
}

/**
 * Applies every record-level data fix above to a `ProjectData` — the SINGLE
 * place that lists them, called from the spots that ever need to:
 * `openAppDB()`'s own `upgrade()` (below, one consolidated pass over every
 * "data" record, regardless of which old version it started at), the
 * `.ehdz` import path (exportProject.ts's `importProject`, also used by
 * driveSyncStore.ts's `reconcile`/`resolveConflict` to read a project stored
 * on Drive — that file format carries the exact same `ProjectData` shape,
 * just JSON-serialized instead of structured-cloned, so it can just as
 * easily have been exported before one of these fixes existed), and
 * `loadSavedProject` below (every normal "reopen my project" read — see its
 * own comment for why `upgrade()` alone isn't enough: it only runs on a
 * `DB_VERSION` bump, and a fix that needs no schema change, like
 * `seriesScale`, can ship without one). No `oldVersion` to gate on for any
 * caller — every fix just runs and no-ops if there's nothing to do (each
 * one's own "same reference back" shape), so this is safe to call
 * unconditionally, regardless of how old (or how recent) the record/file
 * actually is. Adding a future record-level fix means updating ONLY this
 * function — no caller needs to change.
 */
export function migratedProjectData(data: ProjectData): ProjectData {
  const mitecoOrigin = migratedMitecoOrigin(data.mitecoOrigin);
  const subBasins = migratedOutletCode0ToNull(data.project.subBasins);
  const projectWithScale = migratedSeriesScale({ ...data.project, subBasins });
  // `undefined` (no AEMET/ROCIO fetch ever happened) stays `undefined` —
  // never coerced to `{}`, which would falsely look "changed" on every
  // call (a fresh `{}` reference never `===` `undefined`) and needlessly
  // rewrite every such record.
  const aemetProvenance = data.aemetProvenance && migratedAemetProvenance(data.aemetProvenance);
  if (
    mitecoOrigin === data.mitecoOrigin &&
    subBasins === data.project.subBasins &&
    projectWithScale.seriesScale === data.project.seriesScale &&
    aemetProvenance === data.aemetProvenance
  ) {
    return data;
  }
  return { ...data, mitecoOrigin, project: projectWithScale, aemetProvenance };
}

// Real gap found by deep-review (2026-08-29): same class of bug already
// fixed for stationInventoryPromise (aemet.ts) — `??=` caches whatever
// promise openDB() returns on the FIRST call, rejected or not, so a
// failure (blocked upgrade, storage quota, a transient browser issue)
// used to permanently break every save/load for the rest of the session,
// with no way to recover short of a reload. Clearing the cache on
// rejection lets the next call actually retry.
function openAppDB(): Promise<IDBPDatabase<EvalHidDBSchema>> {
  dbPromise ??= openDB<EvalHidDBSchema>(DB_NAME, DB_VERSION, {
    async upgrade(db, oldVersion, _newVersion, transaction) {
      if (oldVersion < 1) {
        db.createObjectStore("meta", { keyPath: "id" });
      }
      if (oldVersion < 2) {
        db.createObjectStore("data", { keyPath: "id" });
        if (oldVersion === 1) {
          // Copy every project already saved under the old "datos" store
          // forward -- "datos" itself is intentionally left in place (not
          // deleted), see EvalHidDBSchema's own comment. Record-level fixes
          // (mitecoOrigin, outletCode...) are NOT applied here — see the
          // single consolidated `migratedProjectData()` pass below, which
          // covers every record regardless of which old version it started
          // at, this copy included.
          const oldStore = transaction.objectStore("datos");
          const newStore = transaction.objectStore("data");
          const records = await oldStore.getAll();
          await Promise.all(records.map((record) => newStore.put(record)));
        }
      }
      if (oldVersion < 4) {
        // "datos" only exists for a database that went through the 1->2
        // rename above — a fresh install jumping straight to this version
        // never created it, so it's not there to delete. By the time this
        // runs, every record it ever held has long been copied into "data"
        // (same transaction, in the `oldVersion < 2` step above — always
        // ahead of this one, even for a user jumping straight from v1).
        if (db.objectStoreNames.contains("datos")) {
          db.deleteObjectStore("datos");
        }
      }
      if (oldVersion < 5) {
        // Splits `series` out of every existing "data" record into the new
        // "series" store (see EvalHidDBSchema's own comment on why) —
        // same "read every record, put() back only what changed" shape as
        // the DB_VERSION 1->2 step above.
        db.createObjectStore("series", { keyPath: "id" });
        const dataStore = transaction.objectStore("data");
        const seriesStore = transaction.objectStore("series");
        const records = await dataStore.getAll();
        await Promise.all(
          records.flatMap((record) => {
            const { series, ...rest } = record;
            return [seriesStore.put({ id: record.id, series }), dataStore.put(rest as ProjectData)];
          })
        );
      }
      if (oldVersion < 6) {
        // Splits `series` further — from "one row per project with every
        // sub-basin's columns together" (DB_VERSION 4->5) to "N rows per
        // project, one per batch of columns, plus one small 'extra' row for
        // pumping/dates" (see EvalHidDBSchema's "seriesBatch"/"seriesExtra"
        // comments for why: a SINGLE `put()` of the whole series blob was
        // itself the clone that could throw "out of memory" at Duero
        // scale). Reads every "data" record just to enumerate ids (the
        // batching itself no longer needs `subBasins` — see
        // seriesBatching.ts's own header) alongside the "series" store the
        // `oldVersion < 5` step just populated — same transaction, so those
        // rows are already fresh even for a database jumping straight from
        // an old version to this one. A record with no matching "series"
        // row (shouldn't happen, but mirrors `loadSavedProject`'s own
        // defensive fallback) is simply skipped — nothing to migrate for
        // it.
        db.createObjectStore("seriesBatch", { keyPath: ["id", "batchIndex"] });
        db.createObjectStore("seriesExtra", { keyPath: "id" });
        const dataStore = transaction.objectStore("data");
        const seriesStore = transaction.objectStore("series");
        const batchStore = transaction.objectStore("seriesBatch");
        const extraStore = transaction.objectStore("seriesExtra");
        const [dataIds, seriesRecords] = await Promise.all([
          dataStore.getAllKeys(),
          seriesStore.getAll(),
        ]);
        const seriesById = new Map(seriesRecords.map((r) => [r.id, r.series]));
        await Promise.all(
          dataIds.flatMap((id) => {
            const series = seriesById.get(id);
            if (!series) return [];
            const { batches, extra } = buildSeriesRows(id, series);
            return [...batches.map((row) => batchStore.put(row)), extraStore.put(extra)];
          })
        );
      }
      if (oldVersion >= 1) {
        // Single consolidated pass for every RECORD-LEVEL fix
        // (`migratedProjectData`'s own comment lists them) — covers every
        // "data" record regardless of which old version it started at
        // (the v1->v2 copy above included, since it no longer applies these
        // fixes inline). The whole point: adding a future fix only means
        // updating `migratedProjectData` itself, never also remembering to
        // wire a new step in here — this loop, and `migratedProjectData`'s
        // own callers for the .ehdz import path, are the ONLY two places
        // that ever need to exist. Safe to run unconditionally on records
        // that don't need it — each individual fix already no-ops via its
        // own "same reference back" contract, so an already-fixed record
        // (e.g. one already handled by this exact step in a PAST upgrade)
        // just round-trips unchanged, and `fixed === record` skips the
        // `put()` below. Skipped for oldVersion 0 (a fresh install): "data"
        // exists but is empty, nothing to fix.
        const store = transaction.objectStore("data");
        const records = await store.getAll();
        await Promise.all(
          records.map((record) => {
            const fixed = migratedProjectData(record as ProjectData);
            return fixed === record ? undefined : store.put(fixed as ProjectData);
          })
        );
      }
    },
  }).catch((err) => {
    dbPromise = null;
    throw err;
  });
  return dbPromise;
}

/** Lightweight list of really-saved projects (excludes ephemeral ones — see ProjectMeta.ephemeral), most recent first — for "Your projects" in Landing.tsx. */
export async function listProjects(): Promise<ProjectMeta[]> {
  const db = await openAppDB();
  const all = await db.getAll("meta");
  return all.filter((p) => !p.ephemeral).sort((a, b) => b.lastModified - a.lastModified);
}

/**
 * A single project's meta record by id, real OR ephemeral — unlike
 * `listProjects()`, doesn't exclude ephemeral rows. Used by
 * `resolveSavedProject` (useProjectPersistence.ts) to resolve a project by
 * id regardless of whether it would ever show up in "Your projects" (the
 * per-project URL restore, projectRoute.ts, deliberately treats real and
 * ephemeral projects the same way) — using `listProjects().find(...)` there
 * used to silently miss an ephemeral project's real name, always falling
 * back to the default one.
 */
export async function getProjectMeta(id: string): Promise<ProjectMeta | null> {
  const db = await openAppDB();
  return (await db.get("meta", id)) ?? null;
}

/** Deletes ephemeral projects (examples, see ProjectMeta.ephemeral) left behind by previous sessions — called once on app startup (App.tsx). */
export async function purgeEphemeralProjects(): Promise<void> {
  const db = await openAppDB();
  const ephemeralProjects = (await db.getAll("meta")).filter((p) => p.ephemeral);
  if (ephemeralProjects.length === 0) return;
  const tx = db.transaction(["meta", "data", "series", "seriesBatch", "seriesExtra"], "readwrite");
  await Promise.all([
    ...ephemeralProjects.flatMap((p) => [
      tx.objectStore("meta").delete(p.id),
      tx.objectStore("data").delete(p.id),
      tx.objectStore("series").delete(p.id),
      tx.objectStore("seriesBatch").delete(seriesBatchKeyRange(p.id)),
      tx.objectStore("seriesExtra").delete(p.id),
    ]),
    tx.done,
  ]);
}

/**
 * Saves (creates or overwrites) a complete project — see the debounced
 * autosave in useProjectPersistence.ts.
 *
 * `opts.includeSeries` (default `true`) lets a caller that KNOWS `series`
 * hasn't changed since the last save skip re-writing it — the whole point
 * of splitting it into its own stores (see EvalHidDBSchema's own comment):
 * a real project's series can be the large majority of its stored bytes,
 * but most edits never touch it, and IndexedDB's `put()` always
 * (re)serializes the full value regardless of what actually changed.
 *
 * **Invariant this relies on**: a project's very FIRST save (creation,
 * import, opening from a folder...) must always use the default
 * (`includeSeries: true`) — `false` is only safe once a "seriesExtra"
 * record is already known to exist for this id, which every caller in this
 * codebase that opts out (useProjectPersistence.ts's own debounced
 * autosave, guarded by a "series changed since the last write" ref)
 * already guarantees. `loadSavedProject` falls back to `EMPTY_SERIES`
 * rather than crashing if this is ever violated, but that's a safety net,
 * not something to rely on.
 *
 * Always clears every existing "seriesBatch" row for this id before
 * writing the current ones (`seriesBatchKeyRange`) — a project that lost
 * sub-basins since its last save (deleted, not just edited) would otherwise
 * leave stale high-`batchIndex` rows behind, carrying columns for
 * sub-basins that no longer exist. The delete and every `put()` below are
 * issued synchronously, in the same tick, all against the same `tx` — not
 * awaited individually — so IndexedDB applies them in the order queued
 * (delete, then the fresh batches) regardless of how their promises later
 * resolve.
 */
export async function saveProject(
  meta: ProjectMeta,
  data: ProjectData,
  opts?: { includeSeries?: boolean }
): Promise<void> {
  const includeSeries = opts?.includeSeries ?? true;
  const db = await openAppDB();
  const { series, ...rest } = data;
  const stores: ("meta" | "data" | "seriesBatch" | "seriesExtra")[] = includeSeries
    ? ["meta", "data", "seriesBatch", "seriesExtra"]
    : ["meta", "data"];
  const tx = db.transaction(stores, "readwrite");
  const puts: Promise<unknown>[] = [
    tx.objectStore("meta").put(meta),
    tx.objectStore("data").put(rest as ProjectData),
  ];
  if (includeSeries) {
    const batchStore = tx.objectStore("seriesBatch");
    const { batches, extra } = buildSeriesRows(data.id, series);
    puts.push(batchStore.delete(seriesBatchKeyRange(data.id)));
    puts.push(...batches.map((row) => batchStore.put(row)));
    puts.push(tx.objectStore("seriesExtra").put(extra));
  }
  await Promise.all([...puts, tx.done]);
}

/** Renames an already-saved project, without touching its data. */
export async function renameSavedProject(id: string, name: string): Promise<void> {
  const db = await openAppDB();
  const meta = await db.get("meta", id);
  if (!meta) return;
  await db.put("meta", { ...meta, name });
}

/**
 * Full data of a saved project, to reopen it — null if it doesn't exist (deleted, invalid id...).
 *
 * Runs `migratedProjectData` here too, not just in `upgrade()` below: `upgrade()` only fires when
 * IndexedDB's own on-disk version number is BELOW the current `DB_VERSION` — a real bug this way
 * once caught a real user (`seriesScale`, added without bumping `DB_VERSION`, since it needs no
 * schema change): anyone whose browser was already at the latest `DB_VERSION` before that field
 * existed never got it backfilled, and `findScaleDateGaps` crashed on `project.seriesScale`
 * being `undefined`. Safe to call unconditionally on every read — see `migratedProjectData`'s own
 * comment on why each individual fix already no-ops when there's nothing to do — and the very next
 * autosave (useProjectPersistence.ts) persists the healed value back, same as `saveProject` never
 * needing its own copy of this call.
 */
export async function loadSavedProject(id: string): Promise<ProjectData | null> {
  const db = await openAppDB();
  const [rest, extra, batchRows] = await Promise.all([
    db.get("data", id),
    db.get("seriesExtra", id),
    db.getAll("seriesBatch", seriesBatchKeyRange(id)),
  ]);
  if (!rest) return null;
  const series = extra ? reassembleSeries(extra, batchRows) : EMPTY_SERIES;
  return migratedProjectData({ ...rest, series });
}

export async function deleteSavedProject(id: string): Promise<void> {
  const db = await openAppDB();
  const tx = db.transaction(["meta", "data", "series", "seriesBatch", "seriesExtra"], "readwrite");
  await Promise.all([
    tx.objectStore("meta").delete(id),
    tx.objectStore("data").delete(id),
    tx.objectStore("series").delete(id),
    tx.objectStore("seriesBatch").delete(seriesBatchKeyRange(id)),
    tx.objectStore("seriesExtra").delete(id),
    tx.done,
  ]);
}

export function newProjectId(): string {
  return crypto.randomUUID();
}
